import React, { useEffect, useId } from 'react'

interface Props {
  title: string
  onClose: () => void
  children: React.ReactNode
  width?: string
}

export default function Modal({ title, onClose, children, width = 'max-w-md' }: Props) {
  const titleId = useId()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    // A sheet anchored to the bottom on a phone and a centred dialog from sm
    // up: a centred box on a small screen either wastes the width or ends up
    // with its buttons under the thumb-unfriendly middle of the display.
    <div
      className="fixed inset-0 bg-ink/40 flex items-end sm:items-center justify-center sm:p-4 z-50"
      onClick={onClose}
    >
      <div
        // Announced as a dialog, named by its title, so a screen reader says
        // what just opened and knows the page behind it is out of play.
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`bg-white border border-line w-full ${width} rounded-t-lg sm:rounded max-h-[92dvh] sm:max-h-[85vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 id={titleId} className="font-display text-lg text-ink">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            // 44px of touch target around a 20px glyph — the close button is
            // the one control every dialog has, and it has to be easy to hit.
            className="text-ink/70 hover:text-ink text-xl leading-none w-11 h-11 -mr-3 flex items-center justify-center"
          >
            ×
          </button>
        </div>
        {/* The extra bottom padding clears the iOS home indicator. */}
        <div className="p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">{children}</div>
      </div>
    </div>
  )
}
